import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterEvent } from '@angular/router';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Clinic, IntakeForm } from 'src/API';
import { safeJsonParse } from 'src/app/core/api/general.util';
import { Patient } from '../api/patient.service';

@Injectable({
  providedIn: 'root'
})
export class AppStateService {
  private routerSubscription: Subscription;

  private currentUrlSubject = new BehaviorSubject<string>('');
  public currentUrl$ = this.currentUrlSubject.asObservable();

  private currentPatientSubject = new BehaviorSubject<Patient | null>(null);
  public currentPatient$ = this.currentPatientSubject.asObservable();

  private patientsSubject = new BehaviorSubject<Patient[]>([]);
  public patients$ = this.patientsSubject.asObservable();

  private currentIntakeFormSubject = new BehaviorSubject<IntakeForm | null>(null);
  public currentIntakeForm$ = this.currentIntakeFormSubject.asObservable();

  private intakeFormsSubject = new BehaviorSubject<IntakeForm[]>([]);
  public intakeForms$ = this.intakeFormsSubject.asObservable();

  private clinicSubject = new BehaviorSubject<Clinic>(null);
  public clinic$ = this.clinicSubject.asObservable();

  private debugMode = false; // For testing

  constructor(private router: Router) {
    this.subscribeToRouterEvents();

    this.currentPatient$.pipe(untilDestroyed(this)).subscribe(updatedPatient => {
      this.updatePatients(updatedPatient);
    });

    this.currentIntakeForm$.pipe(untilDestroyed(this)).subscribe(updatedIntakeForm => {
      this.updateIntakeForms(updatedIntakeForm);
    });

    if (this.debugMode) {
      this.currentPatient$.pipe(untilDestroyed(this)).subscribe(p => {
        console.log(`Current patient updated`, p);
      });
      // this.patients$.pipe(untilDestroyed(this)).subscribe(p => {
      //   console.log(`All patients updated`, p);
      // });
      // this.intakeForms$.pipe(untilDestroyed(this)).subscribe(intakeForms => {
      //   console.log('All intake forms updated', intakeForms);
      // });
      // this.currentIntakeForm$.pipe(untilDestroyed(this)).subscribe(intakeForm => {
      //   console.log('Current intake form updated', intakeForm);
      // });
      this.clinic$.pipe(untilDestroyed(this)).subscribe(clinic => {
        console.log('Clinic updated', clinic);
      });
    }
  }

  private subscribeToRouterEvents(): void {
    this.routerSubscription = this.router.events
      .pipe(filter((event: RouterEvent) => event instanceof NavigationEnd))
      .subscribe(event => {
        const routerState = this.router.routerState;
        const root = routerState.snapshot.root;
        const routeData = this.getAllRouteData(root);
        // May need this for workflow assistant
        // console.log('Route Data:', routeData);
        // console.log('Router event', event);
        //         const navigation = this.router.getCurrentNavigation();
        // const stateData =
        //   navigation && navigation.extras && navigation.extras.state ? navigation.extras.state : {};
        // console.log('state data', stateData);
        // this.currentUrlSubject.next(event.url);

        this.updateAppStateViaRouteData(routeData);
      });
  }

  private getAllRouteData = (route: ActivatedRouteSnapshot) => {
    let data = { ...route.data };
    if (route.firstChild) {
      data = { ...data, ...this.getAllRouteData(route.firstChild) };
    }
    return data;
  };

  private updateAppStateViaRouteData(routeData: any) {
    this.updateCurrentPatientViaRouteData(routeData);
    this.updateCurrentIntakeFormViewRouteData(routeData);
  }

  private updateCurrentIntakeFormViewRouteData(routeData) {
    const intakeForm = routeData.intakeForm;
    if (!intakeForm) {
      this.currentIntakeFormSubject.next(null);
      return;
    }
    this.currentIntakeFormSubject.next(intakeForm);
  }

  private updateCurrentPatientViaRouteData(routeData: any) {
    const routeDataPatient = routeData.patient;
    if (!routeDataPatient) {
      return;
    }
    this.currentPatientSubject.next(routeDataPatient);
  }

  public onOperationIntercepted(operationName, operationData) {
    this.updatePatientsState(operationName, operationData);
    this.updateIntakeFormsState(operationName, operationData);
    this.updateClinicState(operationName, operationData);
  }

  private updateClinicState(operationName, operationData) {
    let currentClinic = this.getClinic();
    switch (operationName) {
      case 'GetAllInfo': {
        this.clinicSubject.next({
          ...currentClinic,
          ...(operationData['me'] ? operationData['me'].clinic : {})
        });
        break;
      }
      case 'UpdateClinic': {
        this.clinicSubject.next({ ...currentClinic, ...operationData['updateClinic'] });
        break;
      }
      case 'UpdateClinicSetup': {
        this.clinicSubject.next({
          ...currentClinic,
          clinicSetup: operationData['updateClinicSetup']
        });
        break;
      }
    }
  }

  private updatePatientsState(operationName, operationData) {
    switch (operationName) {
      case 'UpdatePatient': {
        const incomingPatient = operationData['updatePatient'];
        let currentPatient = this.getCurrentPatient();
        if (currentPatient && incomingPatient) {
          if (incomingPatient.id === currentPatient.id) {
            // Update current patient if id matches
            currentPatient = {
              ...(currentPatient || {}),
              ...incomingPatient,
              questionnaireRequests: this.mergeItems(
                currentPatient.questionnaireRequests,
                incomingPatient.questionnaireRequests
              ),
              assessments: this.mergeItems(currentPatient.assessments, incomingPatient.assessments)
            };
            this.currentPatientSubject.next(currentPatient);
            break;
          }
        }

        this.updatePatients(incomingPatient);
        break;
      }
      case 'CreatePatient': {
        const newPatient = operationData['createPatient'];
        this.updatePatients(newPatient);
        break;
      }
      case 'UpdateQuestionnaireRequest': {
        const updatedQuestionnaireRequest = operationData['updateQuestionnaireRequest'];
        let currentPatient = this.getCurrentPatient();
        if (
          currentPatient &&
          currentPatient.questionnaireRequests &&
          currentPatient.questionnaireRequests.items
        ) {
          let questionnaireRequests = currentPatient.questionnaireRequests.items;
          for (let i = 0; i < questionnaireRequests.length; i++) {
            if (questionnaireRequests[i].id === updatedQuestionnaireRequest.id) {
              currentPatient.questionnaireRequests.items[i] = updatedQuestionnaireRequest;
            }
          }
          this.currentPatientSubject.next(currentPatient);
        }
        break;
      }
      case 'GetPatientsForClinic': {
        this.patientsSubject.next(operationData.getClinic.patients.items);
        break;
      }
    }
  }

  private mergeItems(existing: any, incoming: any) {
    if (!existing || !incoming) return existing || incoming;

    const existingItemsById = new Map<any, any>(
      (existing.items || []).map(item => [item.id, item])
    );

    const mergedItems = [
      ...(existing.items || []).filter(item => !(incoming.items || []).some(i => i.id === item.id)),
      ...(incoming.items || []).map(incomingItem => {
        const existingItem = existingItemsById.get(incomingItem.id);
        return existingItem ? { ...existingItem, ...incomingItem } : incomingItem;
      })
    ];

    return {
      ...existing,
      ...incoming,
      items: mergedItems
    };
  }

  private updateIntakeFormsState(operationName, operationData) {
    switch (operationName) {
      case 'ListIntakeFormByClinicIdSortedByCreatedAt': {
        this.intakeFormsSubject.next(
          operationData.listIntakeFormByClinicIdSortedByCreatedAt.items.map(item => ({
            ...item,
            response: JSON.parse(item.response)
          }))
        );
        break;
      }
      case 'GetIntakeForm': {
        const incomingIntakeForm = operationData['getIntakeForm'];
        this.updateIntakeForms(incomingIntakeForm);
        break;
      }
      case 'UpdateIntakeForm': {
        const incomingIntakeForm = operationData['UpdateIntakeForm'];
        let currentIntakeForm = this.getCurrentIntakeForm();
        if (currentIntakeForm && incomingIntakeForm) {
          if (incomingIntakeForm.id === currentIntakeForm.id) {
            currentIntakeForm = {
              ...(currentIntakeForm || {}),
              ...incomingIntakeForm,
              response: safeJsonParse(incomingIntakeForm.response || '{}')
            };
            this.currentIntakeFormSubject.next(currentIntakeForm);
            break;
          }
        }

        this.updateIntakeForms(incomingIntakeForm);
        break;
      }
    }
  }

  private updatePatients(patient: Patient) {
    this.updateOrAddToArray(patient, this.getPatients(), this.patientsSubject);
  }

  private updateIntakeForms(intakeForm: IntakeForm) {
    this.updateOrAddToArray(intakeForm, this.getIntakeForms(), this.intakeFormsSubject);
  }

  private updateOrAddToArray(element: any, array: any[], arraySubject: BehaviorSubject<any>) {
    if (!element || !element.id) {
      return;
    }
    const elementIndex = array.findIndex(e => {
      if (!e.id) {
        return false;
      }
      return e.id === element.id;
    });
    if (elementIndex !== -1) {
      array[elementIndex] = { ...array[elementIndex], ...element };
    } else if (elementIndex === -1) {
      array.push(element);
    } else {
      console.error('Error updating array state');
    }
    arraySubject.next(array);
  }

  public getClinic() {
    return this.clinicSubject.getValue();
  }

  public getCurrentPatient() {
    return this.currentPatientSubject.getValue();
  }

  public getPatients() {
    return this.patientsSubject.getValue();
  }

  public getCurrentIntakeForm() {
    return this.currentIntakeFormSubject.getValue();
  }

  public getIntakeForms() {
    return this.intakeFormsSubject.getValue();
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }
}
